/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';

/**
 * Destination id of the last hotel listing opened from a featured destination
 * tile.
 *
 * A featured tap is not a search, so the search form must keep showing the
 * user's last real search. The listing params a featured tap produces are shaped
 * exactly like a deeplink's, so nothing in them can tell the two apart — the tap
 * has to record its own provenance, and this is where it goes. Cleared whenever a
 * real search is submitted.
 */
export const FEATURED_DESTINATION_ORIGIN_KEY = [
  QUERY_PERSIST_CACHE,
  'hotels',
  'featuredDestinationOrigin',
] as const;

/** Call site that already holds the query client (e.g. search submit handlers). */
export const clearFeaturedDestinationOrigin = (queryClient: QueryClient) => {
  queryClient.setQueryData<string | null>(
    FEATURED_DESTINATION_ORIGIN_KEY,
    null,
  );
};

export const useFeaturedDestinationOrigin = () => {
  const queryClient = useQueryClient();

  // Never garbage collect: the marker has to outlive the listing round trip.
  queryClient.setQueryDefaults(FEATURED_DESTINATION_ORIGIN_KEY, {
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        // Only trigger a re-render if the specific key we care about changed
        if (
          event.query.queryKey[0] === FEATURED_DESTINATION_ORIGIN_KEY[0] &&
          event.query.queryKey[1] === FEATURED_DESTINATION_ORIGIN_KEY[1] &&
          event.query.queryKey[2] === FEATURED_DESTINATION_ORIGIN_KEY[2]
        ) {
          onStoreChange();
        }
      });
      return unsubscribe;
    },
    [queryClient],
  );

  const getSnapshot = useCallback(
    () =>
      queryClient.getQueryData<string | null>(
        FEATURED_DESTINATION_ORIGIN_KEY,
      ) ?? null,
    [queryClient],
  );

  const featuredDestinationOriginId = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );

  const markFeaturedDestinationOrigin = useCallback(
    (destinationId: string) => {
      queryClient.setQueryData<string | null>(
        FEATURED_DESTINATION_ORIGIN_KEY,
        destinationId || null,
      );
    },
    [queryClient],
  );

  const isFeaturedDestinationOrigin = useCallback(
    (locationId: string | undefined) =>
      Boolean(locationId) && featuredDestinationOriginId === locationId,
    [featuredDestinationOriginId],
  );

  return useMemo(
    () => ({
      featuredDestinationOriginId,
      markFeaturedDestinationOrigin,
      isFeaturedDestinationOrigin,
      clearFeaturedDestinationOrigin: () =>
        clearFeaturedDestinationOrigin(queryClient),
    }),
    [
      featuredDestinationOriginId,
      markFeaturedDestinationOrigin,
      isFeaturedDestinationOrigin,
      queryClient,
    ],
  );
};
